import React, { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TeaserDemoProps } from '../../config/teaserContent';

// Standard animation variants for consistency with performance optimization
export const standardAnimations = {
  pulse: {
    scale: [1, 1.05, 1],
    transition: { duration: 2, repeat: Infinity }
  },
  glow: {
    boxShadow: [
      '0 0 5px rgba(59, 130, 246, 0.3)',
      '0 0 15px rgba(59, 130, 246, 0.6)',
      '0 0 5px rgba(59, 130, 246, 0.3)'
    ],
    transition: { duration: 3, repeat: Infinity }
  },
  loadingFade: {
    opacity: [0.3, 0.7, 0.3],
    transition: { duration: 1.5, repeat: Infinity }
  },
  slideIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.3 }
  }
};

// Performance-optimized component creation pattern
export const createOptimizedTeaserDemo = <T extends TeaserDemoProps>(
  component: React.FC<T>,
  displayName: string
) => {
  const MemoizedComponent = React.memo(component);
  MemoizedComponent.displayName = displayName;
  return MemoizedComponent;
};

// Custom hook for managing teaser demo state
export const useTeaserDemoState = (aspectRatio: 'video' | 'square') => {
  const containerClass = useMemo(() => 
    aspectRatio === 'square' 
      ? 'grid-rows-3 gap-2' // More vertical space for square
      : 'grid-rows-2 gap-3', // More horizontal space for video
    [aspectRatio]
  );
  
  const handleError = useCallback((error: Error, featureName?: string) => {
    console.warn(`[TeaserDemo] ${featureName || 'Component'} error:`, error);
    // Could integrate with Sentry or other monitoring here
  }, []);
  
  return { containerClass, handleError };
};

// Base loading component for teaser demos
export const TeaserDemoLoading: React.FC<{ 
  className?: string; 
  aspectRatio?: 'video' | 'square' 
}> = ({ className, aspectRatio = 'video' }) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);
  
  return (
    <div className={`relative w-full h-full p-3 flex flex-col justify-center ${containerClass} ${className}`}>
      <motion.div
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="w-full flex-1 bg-white/10 rounded-lg min-h-[200px]"
      />
    </div>
  );
};